// Features: M004-F0324, M004-F0325, M004-F0326, M004-F0327, M004-F0328
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { accessControlApi } from '../../services/api';
import StepUpDialog from '../auth/StepUpDialog';
import { getStepUpChallengeFromError } from '../../utils/stepUp';
import './AccessControlPanel.css';

const WEEKDAYS = [
  { value: 1, short: 'Mon', label: 'Monday' },
  { value: 2, short: 'Tue', label: 'Tuesday' },
  { value: 3, short: 'Wed', label: 'Wednesday' },
  { value: 4, short: 'Thu', label: 'Thursday' },
  { value: 5, short: 'Fri', label: 'Friday' },
  { value: 6, short: 'Sat', label: 'Saturday' },
  { value: 7, short: 'Sun', label: 'Sunday' },
];

const RED_CARD_STATES = new Set(['missing', 'revoked', 'stale']);
const AMBER_CARD_STATES = new Set(['unknown']);
const CARD_STATE_ALIASES = new Map([
  ['active', 'active'],
  ['linked', 'active'],
  ['present', 'active'],
  ['ready', 'active'],
  ['missing', 'missing'],
  ['none', 'missing'],
  ['not_linked', 'missing'],
  ['not-linked', 'missing'],
  ['revoked', 'revoked'],
  ['disabled', 'revoked'],
  ['stale', 'stale'],
  ['unknown', 'unknown'],
  ['unavailable', 'unknown'],
  ['pending', 'unknown'],
]);
const TIME_PATTERN = /^(?:[01]\d|2[0-3]):[0-5]\d$/;

const emptyWindow = () => ({
  dayOfWeek: 1,
  startTime: '08:00',
  endTime: '22:00',
  allDay: false,
  overnight: false,
});

const emptyArea = () => ({ name: '', doorIds: [] });
const emptyGroup = () => ({ name: '', areaIds: [], autoAssignable: false, priority: 100, windows: [emptyWindow()] });

const allDayWindows = () => WEEKDAYS.map(({ value }) => ({
  dayOfWeek: value,
  startTime: '00:00',
  endTime: '00:00',
  allDay: true,
  overnight: false,
}));

export const ACCESS_SCHEDULE_TEMPLATES = [
  ...WEEKDAYS.map((day) => ({
    id: `daytime-${day.short.toLowerCase()}`,
    label: `Daytime ${day.short}`,
    group: {
      name: `Daytime ${day.label}`,
      autoAssignable: true,
      priority: 100,
      windows: [{ dayOfWeek: day.value, startTime: '08:00', endTime: '22:00', allDay: false, overnight: false }],
    },
  })),
  {
    id: 'admin-24-7',
    label: 'Admin 24/7',
    group: { name: 'Admin 24/7', autoAssignable: false, priority: 10, windows: allDayWindows() },
  },
  {
    id: 'gym-06-16',
    label: 'Gym 06:00–16:00',
    group: {
      name: 'Gym 06:00–16:00',
      autoAssignable: false,
      priority: 50,
      windows: WEEKDAYS.map(({ value }) => ({
        dayOfWeek: value,
        startTime: '06:00',
        endTime: '16:00',
        allDay: false,
        overnight: false,
      })),
    },
  },
];

const unwrap = (response) => response?.data?.data ?? response?.data ?? response ?? {};
const array = (value) => (Array.isArray(value) ? value : []);
const stringId = (value) => (value === undefined || value === null ? '' : String(value));

const idsFrom = (value) => array(value).map((item) => stringId(
  typeof item === 'object' ? (item.uuid ?? item.doorUuid ?? item.door_uuid ?? item.areaUuid ?? item.area_uuid ?? item.id ?? item.doorId ?? item.door_id ?? item.areaId ?? item.area_id) : item,
)).filter(Boolean);

const looksOpaqueUuid = (value) => /^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(String(value || ''));

// The card projection is intentionally an enum-like health signal. Never echo
// an unexpected backend value because it could contain raw credential material.
const normaliseCardState = (value) => CARD_STATE_ALIASES.get(String(value || 'unknown').trim().toLowerCase()) || 'unknown';

const normaliseWindow = (window = {}) => ({
  dayOfWeek: Number(window.weekday ?? window.isoWeekday ?? window.iso_weekday ?? window.dayOfWeek ?? window.day_of_week ?? 1),
  startTime: window.startTime ?? window.start_time ?? window.start ?? '08:00',
  endTime: window.endTime ?? window.end_time ?? window.end ?? '22:00',
  allDay: Boolean(window.allDay ?? window.all_day),
  overnight: Boolean(window.overnight ?? window.spans_midnight ?? Number(window.endDayOffset ?? window.end_day_offset) === 1) && !Boolean(window.allDay ?? window.all_day),
});

const normaliseAssignment = (assignment = {}) => {
  const origin = String(assignment.origin ?? assignment.provenance ?? assignment.sourceType ?? assignment.source_type ?? 'manual').toLowerCase();
  const sourceCandidate = assignment.source ?? assignment.sourceRef ?? assignment.source_ref;
  const source = sourceCandidate && typeof sourceCandidate === 'object' ? sourceCandidate : {};
  const sourceType = String(
    assignment.sourceType
    ?? assignment.source_type
    ?? source.type
    ?? (origin.includes('room') ? 'room_booking' : origin.includes('term') ? 'term' : origin.includes('class') ? 'class' : ''),
  ).toLowerCase();
  const sourceLabel = assignment.sourceLabel
    ?? assignment.source_label
    ?? source.label
    ?? source.className
    ?? source.class_name
    ?? source.termName
    ?? source.term_name
    ?? source.roomName
    ?? source.room_name
    ?? assignment.className
    ?? assignment.class_name
    ?? assignment.termName
    ?? assignment.term_name
    ?? (typeof sourceCandidate === 'string' && !looksOpaqueUuid(sourceCandidate) ? sourceCandidate : '');

  return {
    groupId: stringId(assignment.groupUuid ?? assignment.group_uuid ?? assignment.groupId ?? assignment.group_id ?? assignment.accessGroupId ?? assignment.access_group_id),
    origin: origin === 'manual' || origin === 'admin' ? 'manual' : 'automatic',
    sourceType,
    sourceLabel: String(sourceLabel || 'linked booking'),
    rowVersion: Number(assignment.rowVersion ?? assignment.row_version ?? 0),
  };
};

export const normaliseAccessOverview = (response, fallbackOrganisation = {}) => {
  const payload = unwrap(response);
  const settings = payload.settings || {};
  const organisation = payload.organisation || {};
  const rawAreas = array(payload.areas);
  const normaliseDoorBinding = (binding = {}) => {
    const installationUuid = stringId(binding.installationUuid ?? binding.installation_uuid);
    const doorRef = stringId(binding.doorRef ?? binding.door_ref ?? binding.doorId ?? binding.door_id ?? binding.id);
    const online = binding.online === undefined ? null : Boolean(binding.online);
    const planCapabilityStatus = String(binding.planCapabilityStatus ?? binding.plan_capability_status ?? 'unconfirmed');
    const enforcementAvailable = binding.scheduleEnforcementAvailable
      ?? binding.schedule_enforcement_available
      ?? binding.capabilities?.scheduleEnforcement
      ?? binding.capabilities?.schedule_enforcement
      ?? (planCapabilityStatus === 'supported');
    const confirmed = binding.confirmed ?? binding.capabilityConfirmed ?? binding.capability_confirmed ?? null;
    const selectable = online === true && enforcementAvailable === true && confirmed !== false;
    const disabledReasonCode = binding.disabledReason
      ?? binding.disabled_reason
      ?? (online === false ? 'Door is offline' : confirmed === false ? 'Capabilities are unconfirmed' : enforcementAvailable === false ? 'Schedule enforcement is unavailable' : selectable ? '' : 'Awaiting confirmed online schedule capability');
    const disabledReasonLabels = {
      offline: 'Door is offline',
      schedule_capability_unconfirmed: 'Schedule capability is unconfirmed',
      schedule_capability_blocked_firmware: 'Installed firmware blocks schedule enforcement',
    };
    return {
      id: installationUuid && doorRef ? `${installationUuid}:${doorRef}` : '',
      installationUuid,
      doorRef,
      name: String(binding.label ?? binding.name ?? doorRef ?? 'Unnamed door'),
      model: String(binding.model ?? binding.deviceModel ?? binding.device_model ?? ''),
      deviceId: stringId(binding.deviceId ?? binding.device_id),
      online,
      planCapabilityStatus,
      enforcementAvailable: enforcementAvailable === true,
      confirmed: confirmed === true,
      selectable,
      disabledReason: String(disabledReasonLabels[disabledReasonCode] ?? disabledReasonCode ?? ''),
      assignedAreaUuid: stringId(binding.assignedAreaUuid ?? binding.assigned_area_uuid),
    };
  };
  const areas = rawAreas.map((area) => {
    const doorBindings = array(area.doorBindings ?? area.door_bindings ?? area.doors).map(normaliseDoorBinding).filter((door) => door.id);
    return {
      id: stringId(area.uuid ?? area.id),
      name: String(area.name || 'Unnamed area'),
      description: String(area.description || ''),
      doorIds: doorBindings.map((door) => door.id),
      doorBindings,
      version: Number(area.rowVersion ?? area.row_version ?? area.version ?? 0),
    };
  }).filter((area) => area.id);
  const candidateDoors = [
    ...rawAreas.flatMap((area) => array(area.doorBindings ?? area.door_bindings ?? area.doors)),
    ...array(payload.availableDoorBindings ?? payload.available_door_bindings ?? payload.doors),
  ].map(normaliseDoorBinding).filter((door) => door.id);
  const doors = [...new Map(candidateDoors.map((door) => [door.id, door])).values()];
  const groups = array(payload.groups ?? payload.accessGroups ?? payload.access_groups).map((group) => ({
    id: stringId(group.uuid ?? group.id),
    name: String(group.name || 'Unnamed access group'),
    areaIds: idsFrom(group.areaUuids ?? group.area_uuids ?? group.areaIds ?? group.area_ids ?? group.areas),
    windows: array(group.windows ?? group.scheduleWindows ?? group.schedule_windows).map(normaliseWindow),
    autoAssignable: Boolean(group.autoAssignable ?? group.auto_assignable),
    priority: Number(group.priority ?? 100),
    version: Number(group.rowVersion ?? group.row_version ?? group.version ?? 0),
  }));

  return {
    version: Number(payload.rowVersion ?? payload.row_version ?? payload.version ?? settings.rowVersion ?? settings.row_version ?? settings.version ?? 0),
    organisation: {
      id: stringId(organisation.id ?? fallbackOrganisation.id),
      name: String(organisation.name ?? fallbackOrganisation.name ?? 'Selected organisation'),
      timezone: String(organisation.timezone ?? payload.timezone ?? settings.timezone ?? 'Europe/London'),
    },
    settings: {
      autoAddEnabled: Boolean(settings.autoAssignmentEnabled ?? settings.auto_assignment_enabled ?? settings.autoAddEnabled ?? settings.auto_add_enabled ?? false),
      arrivalBufferMinutes: Number(settings.arrivalBufferMinutes ?? settings.arrival_buffer_minutes ?? 0),
      departureBufferMinutes: Number(settings.departureBufferMinutes ?? settings.departure_buffer_minutes ?? 0),
      rowVersion: Number(settings.rowVersion ?? settings.row_version ?? payload.rowVersion ?? payload.row_version ?? payload.version ?? 0),
    },
    doors,
    areas,
    groups: groups.filter((group) => group.id),
    members: array(payload.members ?? payload.users).map((member) => {
      const sync = member.sync && typeof member.sync === 'object' ? member.sync : {};
      return ({
      id: stringId(member.userUuid ?? member.user_uuid ?? member.uuid ?? member.userId ?? member.user_id ?? member.id),
      displayName: String(member.displayName ?? member.display_name ?? member.name ?? member.email ?? 'Unnamed member'),
      email: String(member.email ?? member.primaryEmail ?? member.primary_email ?? ''),
      role: String(member.role ?? member.orgRole ?? member.org_role ?? 'member'),
      cardState: normaliseCardState(member.cardState ?? member.card_state),
      needsAttention: Boolean(member.needsAttention ?? member.needs_attention),
      attentionColor: String(member.attentionColor ?? member.attention_color ?? ''),
      syncStatus: String(member.syncStatus ?? member.sync_status ?? sync.status ?? 'unknown').toLowerCase(),
      nextSyncAt: member.nextSyncAt ?? member.next_sync_at ?? sync.nextAt ?? sync.next_at ?? null,
      assignments: array(member.assignments ?? member.groupAssignments ?? member.group_assignments ?? member.accessGroups ?? member.access_groups)
        .map(normaliseAssignment)
        .filter((assignment) => assignment.groupId),
    });
    }).filter((member) => member.id),
  };
};

const validTimezone = (timezone) => {
  try {
    Intl.DateTimeFormat('en-GB', { timeZone: timezone }).format();
    return true;
  } catch (_error) {
    return false;
  }
};

const errorMessage = (error, fallback) => (
  error?.response?.data?.message
  || error?.response?.data?.error?.message
  || error?.message
  || fallback
);

const idempotencyKey = () => (
  globalThis.crypto?.randomUUID?.() || `web-access-${Date.now()}-${Math.random().toString(16).slice(2)}`
);

const windowLabel = (window) => {
  const day = WEEKDAYS.find((candidate) => candidate.value === Number(window.dayOfWeek));
  if (window.allDay) return `${day?.short || 'Day'} all day`;
  return `${day?.short || 'Day'} ${window.startTime}–${window.endTime}${window.overnight ? ' next day' : ''}`;
};

const assignmentLabel = (assignment) => (
  assignment.origin === 'manual'
    ? 'Added by admin'
    : `Automatic — ${assignment.sourceType === 'room_booking' ? 'room booking ' : assignment.sourceType === 'term' ? 'term ' : assignment.sourceType === 'class' ? 'class ' : ''}${assignment.sourceLabel}`
);

const AccessControlPanel = ({ organisationId, organisation = null }) => {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [stepUpChallenge, setStepUpChallenge] = useState(null);
  const [areaDraft, setAreaDraft] = useState(emptyArea);
  const [editingAreaId, setEditingAreaId] = useState('');
  const [groupDraft, setGroupDraft] = useState(emptyGroup);
  const [editingGroupId, setEditingGroupId] = useState('');
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [selectedManualGroupIds, setSelectedManualGroupIds] = useState([]);
  const [memberFilter, setMemberFilter] = useState('all');
  const requestSequence = useRef(0);
  const writeLock = useRef(false);
  const activeOrganisationId = useRef(stringId(organisationId));
  const retryWrite = useRef(null);

  activeOrganisationId.current = stringId(organisationId);

  const loadOverview = useCallback(async ({ signal, message = '' } = {}) => {
    const requestedOrganisationId = stringId(organisationId);
    if (!requestedOrganisationId) return;

    const sequence = ++requestSequence.current;
    setLoading(true);
    setError('');

    try {
      const response = await accessControlApi.getOverview(requestedOrganisationId, { signal });
      if (sequence !== requestSequence.current || activeOrganisationId.current !== requestedOrganisationId) return;
      setOverview(normaliseAccessOverview(response, organisation || {}));
      if (message) setNotice(message);
    } catch (loadError) {
      if (loadError?.code === 'ERR_CANCELED' || loadError?.name === 'CanceledError' || signal?.aborted) return;
      if (sequence !== requestSequence.current || activeOrganisationId.current !== requestedOrganisationId) return;
      setError(errorMessage(loadError, 'Unable to load access settings.'));
    } finally {
      if (sequence === requestSequence.current) setLoading(false);
    }
  }, [organisation, organisationId]);

  useEffect(() => {
    requestSequence.current += 1;
    retryWrite.current = null;
    setOverview(null);
    setError('');
    setNotice('');
    setStepUpChallenge(null);
    setSelectedMemberId('');
    setSelectedManualGroupIds([]);
    setMemberFilter('all');
    setAreaDraft(emptyArea());
    setGroupDraft(emptyGroup());
    setEditingAreaId('');
    setEditingGroupId('');

    if (!organisationId) return undefined;
    const controller = new AbortController();
    loadOverview({ signal: controller.signal });
    return () => controller.abort();
  }, [organisationId, loadOverview]);

  const handleConflict = useCallback(async () => {
    await loadOverview();
    setError('Another administrator changed access settings. The latest version has been loaded; review it before trying again.');
  }, [loadOverview]);

  const performWrite = useCallback(async (operation, successMessage) => {
    if (writeLock.current || !organisationId) return;
    writeLock.current = true;
    const writeOrganisationId = stringId(organisationId);
    setSaving(true);
    setError('');
    setNotice('');

    try {
      await operation();
      retryWrite.current = null;
      setStepUpChallenge(null);
      await loadOverview({ message: successMessage });
    } catch (writeError) {
      const challenge = getStepUpChallengeFromError(writeError);
      if (challenge?.id) {
        retryWrite.current = { organisationId: writeOrganisationId, operation, successMessage };
        setStepUpChallenge(challenge);
      } else if (writeError?.response?.status === 409 || writeError?.response?.data?.code === 'VERSION_CONFLICT') {
        await handleConflict();
      } else {
        setError(errorMessage(writeError, 'Unable to save access settings.'));
      }
    } finally {
      writeLock.current = false;
      setSaving(false);
    }
  }, [handleConflict, loadOverview, organisationId]);

  const finishStepUp = async () => {
    const retry = retryWrite.current;
    setStepUpChallenge(null);
    retryWrite.current = null;
    if (!retry || retry.organisationId !== activeOrganisationId.current) return;
    await performWrite(retry.operation, retry.successMessage);
  };

  const toggleAutoAdd = (event) => {
    const enabled = event.target.checked;
    if (!enabled && !window.confirm(
      'Disable automatic class access? Automatic-only access will be withdrawn. Access added manually will remain.',
    )) return;

    const key = idempotencyKey();
    performWrite(
      () => accessControlApi.updateSettings(organisationId, {
        timezone: overview.organisation.timezone,
        autoAssignmentEnabled: enabled,
        arrivalBufferMinutes: overview.settings.arrivalBufferMinutes,
        departureBufferMinutes: overview.settings.departureBufferMinutes,
        rowVersion: overview.settings.rowVersion,
      }, key),
      enabled ? 'Automatic class access enabled.' : 'Automatic class access disabled; manual access was preserved.',
    );
  };

  const toggleId = (field, id, setter) => setter((current) => ({
    ...current,
    [field]: current[field].includes(id)
      ? current[field].filter((value) => value !== id)
      : [...current[field], id],
  }));

  const submitArea = (event) => {
    event.preventDefault();
    const name = areaDraft.name.trim();
    if (!name) {
      setError('Area name is required.');
      return;
    }
    const currentArea = overview.areas.find((area) => area.id === editingAreaId);
    const body = {
      name,
      description: currentArea?.description || null,
      doorBindings: areaDraft.doorIds.map((doorId) => overview.doors.find((door) => door.id === doorId))
        .filter(Boolean)
        .map((door) => ({ installationUuid: door.installationUuid, doorRef: door.doorRef })),
      ...(editingAreaId ? { rowVersion: currentArea?.version ?? overview.version } : {}),
    };
    const key = idempotencyKey();
    performWrite(
      () => editingAreaId
        ? accessControlApi.updateArea(organisationId, editingAreaId, body, key)
        : accessControlApi.createArea(organisationId, body, key),
      editingAreaId ? 'Access area updated.' : 'Access area created.',
    );
    setAreaDraft(emptyArea());
    setEditingAreaId('');
  };

  const editArea = (area) => {
    setEditingAreaId(area.id);
    setAreaDraft({ name: area.name, doorIds: [...area.doorIds] });
  };

  const deleteArea = (area) => {
    if (!window.confirm(`Delete access area “${area.name}”? Groups using it must be updated first.`)) return;
    const key = idempotencyKey();
    performWrite(
      () => accessControlApi.deleteArea(organisationId, area.id, { rowVersion: area.version }, key),
      'Access area deleted.',
    );
  };

  const updateWindow = (index, field, value) => setGroupDraft((current) => ({
    ...current,
    windows: current.windows.map((window, candidate) => {
      if (candidate !== index) return window;
      if (field === 'allDay') {
        return value
          ? { ...window, allDay: true, overnight: false, startTime: '00:00', endTime: '00:00' }
          : { ...window, allDay: false, startTime: '08:00', endTime: '22:00' };
      }
      return { ...window, [field]: value };
    }),
  }));

  const validateGroup = () => {
    if (!groupDraft.name.trim()) return 'Group name is required.';
    if (groupDraft.windows.length === 0) return 'Add at least one weekly access window.';
    for (const window of groupDraft.windows) {
      if (window.allDay) continue;
      if (!TIME_PATTERN.test(window.startTime) || !TIME_PATTERN.test(window.endTime)) return 'Every window needs valid start and end times.';
      if (window.startTime === window.endTime) return 'Equal start and end times are ambiguous. Choose All day for 24-hour access.';
      if (window.endTime < window.startTime && !window.overnight) return 'A window ending before it starts must be marked Overnight.';
      if (window.endTime > window.startTime && window.overnight) return 'Overnight is only needed when the end time is on the following day.';
    }
    return '';
  };

  const submitGroup = (event) => {
    event.preventDefault();
    const validationError = validateGroup();
    if (validationError) {
      setError(validationError);
      return;
    }
    const body = {
      name: groupDraft.name.trim(),
      description: null,
      timezone: overview.organisation.timezone,
      autoAssignable: groupDraft.autoAssignable,
      priority: Number(groupDraft.priority),
      areaUuids: groupDraft.areaIds,
      windows: groupDraft.windows.map((window) => ({
        weekday: Number(window.dayOfWeek),
        startTime: window.allDay ? '00:00' : window.startTime,
        endTime: window.allDay ? '00:00' : window.endTime,
        allDay: Boolean(window.allDay),
        endDayOffset: window.allDay || window.overnight ? 1 : 0,
      })),
      ...(editingGroupId ? { rowVersion: overview.groups.find((group) => group.id === editingGroupId)?.version ?? overview.version } : {}),
    };
    body.windows = body.windows.map(({ startTime, endTime, ...window }) => ({ ...window, start: startTime, end: endTime }));
    const key = idempotencyKey();
    performWrite(
      () => editingGroupId
        ? accessControlApi.updateGroup(organisationId, editingGroupId, body, key)
        : accessControlApi.createGroup(organisationId, body, key),
      editingGroupId ? 'Access group updated.' : 'Access group created.',
    );
    setGroupDraft(emptyGroup());
    setEditingGroupId('');
  };

  const editGroup = (group) => {
    setEditingGroupId(group.id);
    setGroupDraft({
      name: group.name,
      areaIds: [...group.areaIds],
      autoAssignable: group.autoAssignable,
      priority: group.priority,
      windows: group.windows.length ? group.windows.map((window) => ({ ...window })) : [emptyWindow()],
    });
  };

  const deleteGroup = (group) => {
    if (!window.confirm(`Delete access group “${group.name}”? Its grants will be withdrawn.`)) return;
    const key = idempotencyKey();
    performWrite(
      () => accessControlApi.deleteGroup(organisationId, group.id, { rowVersion: group.version }, key),
      'Access group deleted.',
    );
  };

  const selectTemplate = (template) => {
    setEditingGroupId('');
    setGroupDraft((current) => ({
      ...emptyGroup(),
      areaIds: [...current.areaIds],
      name: template.group.name,
      autoAssignable: Boolean(template.group.autoAssignable),
      priority: Number(template.group.priority ?? 100),
      windows: template.group.windows.map((window) => ({ ...window })),
    }));
  };

  const selectedMember = useMemo(() => (
    overview?.members.find((member) => member.id === selectedMemberId) || null
  ), [overview, selectedMemberId]);

  const filteredMembers = useMemo(() => {
    if (!overview) return [];
    return overview.members.filter((member) => {
      if (memberFilter === 'attention') return member.needsAttention || member.attentionColor === 'red' || RED_CARD_STATES.has(member.cardState);
      if (memberFilter === 'manual') return member.assignments.some((assignment) => assignment.origin === 'manual');
      if (memberFilter === 'class') return member.assignments.some((assignment) => assignment.origin === 'automatic' && assignment.sourceType === 'class');
      if (memberFilter === 'term') return member.assignments.some((assignment) => assignment.origin === 'automatic' && assignment.sourceType === 'term');
      if (memberFilter === 'room_booking') return member.assignments.some((assignment) => assignment.origin === 'automatic' && assignment.sourceType === 'room_booking');
      return true;
    });
  }, [memberFilter, overview]);

  const chooseMember = (memberId) => {
    setSelectedMemberId(memberId);
    const member = overview.members.find((candidate) => candidate.id === memberId);
    setSelectedManualGroupIds(member
      ? member.assignments.filter((assignment) => assignment.origin === 'manual').map((assignment) => assignment.groupId)
      : []);
  };

  const saveManualGroups = (event) => {
    event.preventDefault();
    if (!selectedMember) return;
    const original = new Set(selectedMember.assignments
      .filter((assignment) => assignment.origin === 'manual')
      .map((assignment) => assignment.groupId));
    const selected = new Set(selectedManualGroupIds);
    const additions = [...selected].filter((groupId) => !original.has(groupId));
    const removals = [...original].filter((groupId) => !selected.has(groupId));
    if (!additions.length && !removals.length) {
      setNotice('No manual access changes to save.');
      return;
    }

    const additionWrites = additions.map((groupId) => ({ groupId, key: idempotencyKey() }));
    const removalWrites = removals.map((groupId) => ({ groupId, key: idempotencyKey() }));
    performWrite(async () => {
      for (const { groupId, key } of additionWrites) {
        await accessControlApi.addManualGroupMember(organisationId, groupId, selectedMember.id, {}, key);
      }
      for (const { groupId, key } of removalWrites) {
        const assignment = selectedMember.assignments.find((candidate) => candidate.groupId === groupId && candidate.origin === 'manual');
        await accessControlApi.removeManualGroupMember(organisationId, groupId, selectedMember.id, {
          rowVersion: assignment?.rowVersion,
        }, key);
      }
    }, 'Manual access updated. Automatic class access was not changed.');
  };

  if (!organisationId) {
    return (
      <section className="access-empty" data-testid="WEB-ACCESS-002">
        <h2>Choose one organisation</h2>
        <p>Access settings are tenant-specific. Select an organisation above before viewing or changing door access.</p>
      </section>
    );
  }

  if (loading && !overview) return <div className="loading-spinner" data-testid="WEB-ACCESS-003">Loading access settings...</div>;

  if (!overview) {
    return (
      <section className="access-empty">
        <h2>Access settings unavailable</h2>
        {error && <div className="access-message error" role="alert">{error}</div>}
        <button type="button" className="btn btn-outline" onClick={() => loadOverview()}>Try again</button>
      </section>
    );
  }

  const timezoneOkay = validTimezone(overview.organisation.timezone);

  return (
    <section className="access-control-panel" data-testid="WEB-ACCESS-001">
      <header className="access-panel-header">
        <div>
          <h2>Door access</h2>
          <p>{overview.organisation.name} · schedules use <strong>{overview.organisation.timezone}</strong></p>
        </div>
        <button type="button" className="btn btn-outline" onClick={() => loadOverview()} disabled={loading || saving}>Refresh</button>
      </header>

      {!timezoneOkay && (
        <div className="access-message error" role="alert">The organisation timezone is invalid. Schedule editing is disabled until it is corrected.</div>
      )}
      {error && <div className="access-message error" role="alert">{error}</div>}
      {notice && <div className="access-message success" role="status">{notice}</div>}

      <section className="access-section access-settings" data-testid="WEB-ACCESS-004">
        <div>
          <h3>Automatic class access</h3>
          <p>Core matches class and term bookings to the best schedule group. The browser never calculates access.</p>
        </div>
        <label className="access-toggle">
          <input
            type="checkbox"
            checked={overview.settings.autoAddEnabled}
            onChange={toggleAutoAdd}
            disabled={saving}
          />
          <span>{overview.settings.autoAddEnabled ? 'Enabled' : 'Disabled'}</span>
        </label>
      </section>

      <section className="access-section" data-testid="WEB-ACCESS-005">
        <div className="access-section-heading">
          <div>
            <h3>Areas and doors</h3>
            <p>Areas collect one or more controller-provided doors. Offline state is informational; Core remains authoritative.</p>
          </div>
        </div>
        <div className="access-card-grid">
          {overview.areas.map((area) => (
            <article className="access-card" key={area.id}>
              <h4>{area.name}</h4>
              <p>{area.doorIds.length} door{area.doorIds.length === 1 ? '' : 's'}</p>
              <div className="access-inline-actions">
                <button type="button" className="btn btn-outline" onClick={() => editArea(area)} disabled={saving}>Edit</button>
                <button type="button" className="btn btn-danger" onClick={() => deleteArea(area)} disabled={saving}>Delete</button>
              </div>
            </article>
          ))}
        </div>
        <form className="access-editor" onSubmit={submitArea}>
          <h4>{editingAreaId ? 'Edit area' : 'Create area'}</h4>
          <label>
            Area name
            <input value={areaDraft.name} onChange={(event) => setAreaDraft((current) => ({ ...current, name: event.target.value }))} maxLength="120" required />
          </label>
          <fieldset>
            <legend>Doors</legend>
            <div className="access-check-grid">
              {overview.doors.map((door) => (
                <label key={door.id}>
                  <input
                    type="checkbox"
                    checked={areaDraft.doorIds.includes(door.id)}
                    onChange={() => toggleId('doorIds', door.id, setAreaDraft)}
                    disabled={(!door.selectable || (door.assignedAreaUuid && door.assignedAreaUuid !== editingAreaId)) && !areaDraft.doorIds.includes(door.id)}
                  />
                  <span>
                    {door.name}{door.model ? ` · ${door.model}` : ''}
                    {door.assignedAreaUuid && door.assignedAreaUuid !== editingAreaId
                      ? <small className="access-door-reason">Already assigned to another area</small>
                      : !door.selectable && <small className="access-door-reason">{door.disabledReason}</small>}
                  </span>
                </label>
              ))}
              {!overview.doors.length && <p>No confirmed door catalogue is available yet. Complete Mycelium door onboarding and capability discovery first.</p>}
            </div>
          </fieldset>
          <div className="access-inline-actions">
            <button className="btn btn-primary" type="submit" disabled={saving}>{editingAreaId ? 'Save area' : 'Create area'}</button>
            {editingAreaId && <button type="button" className="btn btn-outline" onClick={() => { setEditingAreaId(''); setAreaDraft(emptyArea()); }}>Cancel</button>}
          </div>
        </form>
      </section>

      <section className="access-section" data-testid="WEB-ACCESS-006">
        <div className="access-section-heading">
          <div>
            <h3>Schedule groups</h3>
            <p>Windows are weekly, half-open intervals: access is valid at the start and stops at the end.</p>
          </div>
        </div>
        <div className="access-card-grid">
          {overview.groups.map((group) => (
            <article className="access-card" key={group.id}>
              <h4>{group.name}</h4>
              <p>{group.windows.map(windowLabel).join(' · ') || 'No schedule windows'}</p>
              <p>{group.areaIds.length} area{group.areaIds.length === 1 ? '' : 's'} · priority {group.priority}{group.autoAssignable ? ' · automatic matching' : ''}</p>
              <div className="access-inline-actions">
                <button type="button" className="btn btn-outline" onClick={() => editGroup(group)} disabled={saving}>Edit</button>
                <button type="button" className="btn btn-danger" onClick={() => deleteGroup(group)} disabled={saving}>Delete</button>
              </div>
            </article>
          ))}
        </div>

        <div className="access-template-list" aria-label="Schedule templates">
          {ACCESS_SCHEDULE_TEMPLATES.map((template) => (
            <button type="button" className="access-template" key={template.id} onClick={() => selectTemplate(template)} disabled={saving || !timezoneOkay}>
              {template.label}
            </button>
          ))}
        </div>

        <form className="access-editor" onSubmit={submitGroup}>
          <h4>{editingGroupId ? 'Edit schedule group' : 'Create schedule group'}</h4>
          <label>
            Group name
            <input value={groupDraft.name} onChange={(event) => setGroupDraft((current) => ({ ...current, name: event.target.value }))} maxLength="120" required />
          </label>
          <fieldset>
            <legend>Areas</legend>
            <div className="access-check-grid">
              {overview.areas.map((area) => (
                <label key={area.id}>
                  <input type="checkbox" checked={groupDraft.areaIds.includes(area.id)} onChange={() => toggleId('areaIds', area.id, setGroupDraft)} />
                  {area.name}
                </label>
              ))}
            </div>
          </fieldset>
          <div className="access-group-policy-fields">
            <label>
              <input
                type="checkbox"
                checked={groupDraft.autoAssignable}
                onChange={(event) => setGroupDraft((current) => ({ ...current, autoAssignable: event.target.checked }))}
              />
              Available for automatic booking matching
            </label>
            <label>
              Matching priority (lower wins)
              <input
                type="number"
                min="0"
                max="65535"
                value={groupDraft.priority}
                onChange={(event) => setGroupDraft((current) => ({ ...current, priority: Number(event.target.value) }))}
              />
            </label>
          </div>
          <fieldset disabled={!timezoneOkay}>
            <legend>Weekly windows ({overview.organisation.timezone})</legend>
            <div className="access-window-list">
              {groupDraft.windows.map((window, index) => (
                <div className="access-window" key={`${index}-${window.dayOfWeek}`}>
                  <select aria-label={`Window ${index + 1} day`} value={window.dayOfWeek} onChange={(event) => updateWindow(index, 'dayOfWeek', Number(event.target.value))}>
                    {WEEKDAYS.map((day) => <option value={day.value} key={day.value}>{day.label}</option>)}
                  </select>
                  <input aria-label={`Window ${index + 1} start`} type="time" value={window.allDay ? '00:00' : window.startTime} onChange={(event) => updateWindow(index, 'startTime', event.target.value)} disabled={window.allDay} />
                  <span>to</span>
                  <input aria-label={`Window ${index + 1} end`} type="time" value={window.allDay ? '00:00' : window.endTime} onChange={(event) => updateWindow(index, 'endTime', event.target.value)} disabled={window.allDay} />
                  <label><input type="checkbox" checked={window.allDay} onChange={(event) => updateWindow(index, 'allDay', event.target.checked)} />All day</label>
                  <label><input type="checkbox" checked={window.overnight} onChange={(event) => updateWindow(index, 'overnight', event.target.checked)} disabled={window.allDay} />Overnight</label>
                  <button type="button" className="btn btn-outline" aria-label={`Remove window ${index + 1}`} onClick={() => setGroupDraft((current) => ({ ...current, windows: current.windows.filter((_item, candidate) => candidate !== index) }))}>Remove</button>
                </div>
              ))}
            </div>
            <button type="button" className="btn btn-outline" onClick={() => setGroupDraft((current) => ({ ...current, windows: [...current.windows, emptyWindow()] }))}>+ Add window</button>
          </fieldset>
          <div className="access-inline-actions">
            <button className="btn btn-primary" type="submit" disabled={saving || !timezoneOkay}>{editingGroupId ? 'Save group' : 'Create group'}</button>
            {editingGroupId && <button type="button" className="btn btn-outline" onClick={() => { setEditingGroupId(''); setGroupDraft(emptyGroup()); }}>Cancel</button>}
          </div>
        </form>
      </section>

      <section className="access-section" data-testid="WEB-ACCESS-007">
        <div className="access-section-heading">
          <div>
            <h3>Members</h3>
            <p>Add any current organisation member to one or more groups. Card identifiers are intentionally not shown.</p>
          </div>
        </div>
        <form className="access-member-editor" onSubmit={saveManualGroups}>
          <label>
            Organisation member
            <select value={selectedMemberId} onChange={(event) => chooseMember(event.target.value)}>
              <option value="">Choose a member</option>
              {overview.members.map((member) => <option value={member.id} key={member.id}>{member.displayName}{member.email ? ` — ${member.email}` : ''}</option>)}
            </select>
          </label>
          {selectedMember && (
            <fieldset>
              <legend>Groups added manually</legend>
              <div className="access-check-grid">
                {overview.groups.map((group) => (
                  <label key={group.id}>
                    <input
                      type="checkbox"
                      checked={selectedManualGroupIds.includes(group.id)}
                      onChange={() => setSelectedManualGroupIds((current) => current.includes(group.id) ? current.filter((id) => id !== group.id) : [...current, group.id])}
                    />
                    {group.name}
                  </label>
                ))}
              </div>
              <p className="access-help">Automatic class or term assignments are shown below and cannot be removed here.</p>
            </fieldset>
          )}
          <button type="submit" className="btn btn-primary" disabled={!selectedMember || saving}>Save manual access</button>
        </form>

        <label className="access-member-filter">
          Show
          <select value={memberFilter} onChange={(event) => setMemberFilter(event.target.value)}>
            <option value="all">All members</option>
            <option value="attention">Card information needs attention</option>
            <option value="manual">Added by admin</option>
            <option value="class">Automatic — class</option>
            <option value="term">Automatic — term</option>
            <option value="room_booking">Automatic — room booking</option>
          </select>
        </label>

        <div className="access-member-table-wrap">
          <table className="access-member-table">
            <thead><tr><th>Member</th><th>Card</th><th>Access groups</th></tr></thead>
            <tbody>
              {filteredMembers.map((member) => {
                const red = member.needsAttention || member.attentionColor === 'red' || RED_CARD_STATES.has(member.cardState);
                const amber = AMBER_CARD_STATES.has(member.cardState);
                return (
                  <tr key={member.id} className={red ? 'card-attention' : amber ? 'card-unknown' : ''} data-testid={`access-member-${member.id}`}>
                    <td><strong>{member.displayName}</strong><span>{member.email}</span><small>{member.role}</small></td>
                    <td>
                      <span className={`access-card-state ${red ? 'attention' : amber ? 'unknown' : 'ready'}`}>{member.cardState}</span>
                      {red && <strong className="access-attention-text">Card information needs attention</strong>}
                      {amber && <span className="access-unknown-text">Card status has not been confirmed</span>}
                      {red && <span className="access-sync-note">Saved in StudioAEye; not sent to Mycelium until an active card is linked.</span>}
                      {!red && member.syncStatus !== 'unknown' && (
                        <span className={`access-sync-note ${member.syncStatus}`}>
                          {member.syncStatus === 'applied' ? 'Applied to access controller' : member.syncStatus === 'degraded' ? 'Controller sync degraded' : 'Pending controller sync'}
                          {member.nextSyncAt ? ` · next window ${new Date(member.nextSyncAt).toLocaleString('en-GB', { timeZone: overview.organisation.timezone })}` : ''}
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="access-assignment-list">
                        {member.assignments.map((assignment, index) => (
                          <span className={`access-origin ${assignment.origin}`} key={`${assignment.groupId}-${assignment.origin}-${index}`}>
                            {overview.groups.find((group) => group.id === assignment.groupId)?.name || 'Access group'} · {assignmentLabel(assignment)}
                          </span>
                        ))}
                        {!member.assignments.length && <span>No access groups</span>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <StepUpDialog
        challenge={stepUpChallenge}
        title="Verify this access-control change"
        description="Door access changes require a fresh high-security verification."
        onComplete={finishStepUp}
        onCancel={() => { retryWrite.current = null; setStepUpChallenge(null); }}
      />
    </section>
  );
};

export default AccessControlPanel;
